import { useMutation, useQueryClient, UseMutationResult } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { toast } from 'sonner';
import { getEmployeeUseCases } from '@personnel/composition';
import type { EmployeeDeleteResponseDto } from '@personnel/application/dtos';
import { useApiErrorHandler } from './useApiErrorHandler';
import { ROLES_QUERY_KEYS } from './useRoleList';

/**
 * Query keys for employee cache management
 */
export const EMPLOYEES_QUERY_KEYS = {
  list: () => ['personnel', 'employees', 'list'] as const,
  detail: (uuid: string) => ['personnel', 'employees', 'detail', uuid] as const,
};

/**
 * Hook options for configuring delete behavior
 */
export interface UseDeleteEmployeeOptions {
  onSuccess?: (uuid: string) => void;
  onError?: (error: Error, uuid: string) => void;
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
}

/**
 * Hook result with mutation function and states
 */
export interface UseDeleteEmployeeResult {
  deleteEmployee: (uuid: string) => Promise<void>;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: Error | null;
  reset: () => void;
}

/**
 * Hook for deleting an employee
 *
 * Uses React Query mutation to handle the employee deletion flow.
 * Automatically invalidates the employees list cache on success.
 *
 * @param {UseDeleteEmployeeOptions} options - Hook configuration options
 * @returns {UseDeleteEmployeeResult} React Query mutation result with typed interface
 *
 * @example
 * const { deleteEmployee, isLoading } = useDeleteEmployee({
 *   onSuccess: (uuid) => console.log('Deleted:', uuid),
 *   showSuccessToast: true
 * });
 *
 * const handleDelete = async (uuid: string) => {
 *   await deleteEmployee(uuid);
 * };
 */
export const useDeleteEmployee = (options: UseDeleteEmployeeOptions = {}): UseDeleteEmployeeResult => {
  const { onSuccess, onError, showSuccessToast = true, showErrorToast = true } = options;

  const queryClient = useQueryClient();
  const { t } = useTranslation('personnel-employees');
  const { handleError } = useApiErrorHandler();
  const { delete: deleteUseCase } = getEmployeeUseCases();

  const mutation: UseMutationResult<EmployeeDeleteResponseDto, Error, string> = useMutation({
    mutationFn: async (uuid: string) => {
      return deleteUseCase.execute({ userUuid: uuid });
    },
    onSuccess: (response) => {
      // Invalidate employees list to refresh the table
      queryClient.invalidateQueries(EMPLOYEES_QUERY_KEYS.list());
      // Remove deleted employee from detail cache
      queryClient.removeQueries(EMPLOYEES_QUERY_KEYS.detail(response.deletedUuid));
      // Invalidate roles list to refresh usersCount
      queryClient.invalidateQueries(ROLES_QUERY_KEYS.list());

      if (showSuccessToast) {
        toast.success(t('notifications.deleteSuccess'), { duration: 3000 });
      }

      if (onSuccess) {
        onSuccess(response.deletedUuid);
      }
    },
    onError: (error: Error, uuid: string) => {
      if (showErrorToast) {
        handleError(error);
      }

      if (onError) {
        onError(error, uuid);
      }
    },
  });

  /**
   * Deletes an employee by UUID
   * Wraps mutation to maintain void return type for backwards compatibility
   * Errors are handled by the mutation's onError callback, so we catch and suppress them here
   * to prevent unhandled promise rejections that could show error screens
   */
  const deleteEmployee = useCallback(
    async (uuid: string): Promise<void> => {
      try {
        await mutation.mutateAsync(uuid);
      } catch (error) {
        // Error is already handled by mutation's onError callback
        // We catch it here to prevent unhandled promise rejections
        // that could trigger error boundaries or show error screens
      }
    },
    [mutation],
  );

  return {
    deleteEmployee,
    isLoading: mutation.isLoading,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error || null,
    reset: mutation.reset,
  };
};
